/**
 * orgChartLevels — the PURE reporting-hierarchy layout for the org chart.
 *
 * Extracted from `components/IntakeModule.tsx`'s `OrgChart` so the level
 * computation can be unit-tested (the JSX component itself can't load in the
 * pure Node test harness). No React / Firebase imports — only the `PersonInput`
 * TYPE, which is erased at transpile — so `_tsload` loads this module directly.
 *
 * The algorithm is unchanged from the in-component original, with ONE additive
 * guard: a reporting RING with no root (A→B→C→A) would otherwise leave zero
 * levels and silently DROP every member — so a no-root edge set falls back to
 * the flat seniority-bucket layout. The has-root path is behaviour-identical.
 */
import { type PersonInput } from './intake';

/** Work-role seniority rank — lower = more senior (placed higher). */
export const WORK_RANK: Record<string, number> = {
  founder: 0, co_founder: 0, proprietor: 0, partner: 0, chairman: 0,
  director: 1, ceo: 1, coo: 1, cfo: 1,
  general_manager: 2, manager: 2, team_lead: 2, supervisor: 2,
  employee: 3, accountant: 3,
};

/** Roles rendered on the dotted "external" side-link, not in the reporting tree. */
export const EXTERNAL_ROLES: ReadonlySet<string> = new Set(['consultant', 'vendor', 'contractor']);

/** Flat seniority-bucket layout — the fallback when there are no reporting edges/roots. */
function seniorityLevels(internals: PersonInput[]): PersonInput[][] {
  const buckets = new Map<number, PersonInput[]>();
  for (const p of internals) {
    const rank = WORK_RANK[p.work_role || ''] ?? 4;
    const arr = buckets.get(rank) || [];
    arr.push(p);
    buckets.set(rank, arr);
  }
  return [...buckets.keys()].sort((a, b) => a - b).map((k) => buckets.get(k)!);
}

/**
 * PURE — compute the top-down reporting levels for the INTERNAL members (the
 * caller splits externals off first). Top-down by `reports_to` where real edges
 * exist, else by work-role seniority. Ring-safe: an edge set with no root falls
 * back to seniority so no member is ever dropped.
 */
export function computeOrgLevels(internals: PersonInput[]): PersonInput[][] {
  const byId = new Map(internals.map((p) => [p.id, p]));
  const hasEdges = internals.some((p) => p.reports_to && byId.has(p.reports_to));
  if (!hasEdges) return seniorityLevels(internals);

  // BFS from roots (no reports_to, or reports_to outside the set).
  const childrenOf = new Map<string, PersonInput[]>();
  const roots: PersonInput[] = [];
  for (const p of internals) {
    if (p.reports_to && byId.has(p.reports_to)) {
      const arr = childrenOf.get(p.reports_to) || [];
      arr.push(p);
      childrenOf.set(p.reports_to, arr);
    } else {
      roots.push(p);
    }
  }

  // No root at all → a pure reporting ring. Falling through the BFS would render
  // nothing and drop every member; use the seniority layout so all still appear.
  if (roots.length === 0) return seniorityLevels(internals);

  const levels: PersonInput[][] = [];
  let frontier = roots;
  const seen = new Set<string>();
  while (frontier.length && levels.length < 8) {
    const row = frontier.filter((p) => !seen.has(p.id));
    row.forEach((p) => seen.add(p.id));
    if (!row.length) break;
    levels.push(row);
    frontier = row.flatMap((p) => childrenOf.get(p.id) || []);
  }
  return levels;
}
